import { useCallback, useEffect, useRef, useState } from "react";

import {
  actionNotificationEvent,
  type ActionNotificationDetail,
} from "@/services/actionNotifications";

import { ToastRegion, type ToastMessage } from "./ToastRegion";

const NOTIFICATION_DURATION_MS = 12_000;

export function ActionNotificationCenter() {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setMessages((current) => current.filter((message) => message.id !== id));
  }, []);

  useEffect(() => {
    const currentTimers = timers.current;
    const notify = (event: Event): void => {
      const { message, tone } = (event as CustomEvent<ActionNotificationDetail>).detail;
      const id = crypto.randomUUID();
      setMessages((current) => [...current.slice(-3), { id, message, tone }]);
      currentTimers.set(
        id,
        setTimeout(() => dismiss(id), NOTIFICATION_DURATION_MS)
      );
    };
    window.addEventListener(actionNotificationEvent, notify);
    return () => {
      window.removeEventListener(actionNotificationEvent, notify);
      currentTimers.forEach(clearTimeout);
      currentTimers.clear();
    };
  }, [dismiss]);

  return <ToastRegion messages={messages} onDismiss={dismiss} />;
}
